import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { X, Send, CheckCircle2, Loader2, AlertCircle, Camera, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { useTheme } from '../../styles/ThemeContext';
export interface PhotoData {
  id: string;
  beforeUrl: string;
  afterUrl?: string;
  status: 'processing' | 'ready' | 'failed';
  timestamp: number;
}
interface PhotoReviewPanelProps {
  isOpen: boolean;
  onClose: () => void;
  photos: PhotoData[];
  onSendEmail: (email: string) => Promise<void>;
}
export function PhotoReviewPanel({
  isOpen,
  onClose,
  photos,
  onSendEmail
}: PhotoReviewPanelProps) {
  const {
    isDark
  } = useTheme();
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [activePhotoId, setActivePhotoId] = useState<string | null>(null);
  // Slider state: 0 = full AFTER (AI), 100 = full BEFORE (Original)
  const [sliderPosition, setSliderPosition] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Hint state
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const sliderControls = useAnimation();
  useEffect(() => {
    if (photos.length > 0 && !activePhotoId) {
      setActivePhotoId(photos[0].id);
    }
  }, [photos, activePhotoId]);
  const activePhoto = photos.find(p => p.id === activePhotoId) || photos[0];
  // Reset interaction state when changing photos
  useEffect(() => {
    setHasInteracted(false);
    setSliderPosition(0);
    setShowHint(false);
  }, [activePhotoId]);
  // Handle hint and auto-animation
  useEffect(() => {
    if (activePhoto?.status === 'ready' && !hasInteracted && isOpen) {
      setShowHint(true);
      // Auto-animate after 2 seconds
      const animTimer = setTimeout(() => {
        if (!hasInteracted) {
          sliderControls.start({
            left: ['0%', '20%', '0%'],
            transition: {
              duration: 1.5,
              ease: 'easeInOut'
            }
          }).then(() => {
            // Ensure state matches DOM after animation if not interacted
            if (!hasInteracted) setSliderPosition(0);
          });
        }
      }, 2000);
      // Hide hint after 6 seconds
      const hideTimer = setTimeout(() => {
        setShowHint(false);
      }, 6000);
      return () => {
        clearTimeout(animTimer);
        clearTimeout(hideTimer);
        sliderControls.stop();
      };
    }
  }, [activePhoto?.status, hasInteracted, isOpen, sliderControls]);
  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    setHasInteracted(true);
    setShowHint(false);
    sliderControls.stop(); // Stop any ongoing auto-animation
  };
  const handlePointerUp = () => {
    setIsDragging(false);
  };
  const handlePointerMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging || !sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = x / rect.width * 100;
    setSliderPosition(percentage);
  };
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handlePointerMove);
      window.addEventListener('mouseup', handlePointerUp);
      window.addEventListener('touchmove', handlePointerMove);
      window.addEventListener('touchend', handlePointerUp);
    } else {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', handlePointerUp);
    }
    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', handlePointerUp);
    };
  }, [isDragging]);
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsSending(true);
    await onSendEmail(email);
    setIsSending(false);
    setSendSuccess(true);
    setTimeout(() => setSendSuccess(false), 3000);
  };
  return <AnimatePresence data-id="element-205">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/95 backdrop-blur-xl z-[100] flex flex-col" data-id="element-206">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-20 p-6 bg-gradient-to-b from-black/80 to-transparent" data-id="element-207">
            <div className="flex items-center justify-between max-w-7xl mx-auto" data-id="element-208">
              <div className="flex items-center gap-3" data-id="element-209">
                <div className="p-2 rounded-full bg-cyan-500/20 text-cyan-400" data-id="element-210">
                  <Camera className="w-5 h-5" data-id="element-211" />
                </div>
                <div data-id="element-212">
                  <h2 className="text-xl font-light text-white" data-id="element-213">
                    AI{' '}
                    <span className="font-bold text-cyan-400" data-id="element-214">
                      Photographer
                    </span>
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-id="element-215">
                <X className="w-6 h-6" data-id="element-216" />
              </button>
            </div>
          </div>

          {/* Main Image Area */}
          <div className="flex-1 flex items-center justify-center p-6 pt-24 pb-32" data-id="element-217">
            {activePhoto && <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="relative w-full max-w-5xl aspect-[16/10] rounded-xl overflow-hidden shadow-2xl bg-slate-900 select-none border border-white/10" ref={sliderRef} data-id="element-218">
                {/* Base Layer: AFTER Image (AI Enhanced) */}
                {activePhoto.afterUrl && activePhoto.status === 'ready' ? <img src={activePhoto.afterUrl} alt="AI Enhanced" className="absolute inset-0 w-full h-full object-cover pointer-events-none" data-id="element-219" /> : <img src={activePhoto.beforeUrl} alt="Original" className="absolute inset-0 w-full h-full object-cover pointer-events-none opacity-50" data-id="element-220" />}

                {/* Overlay Layer: BEFORE Image (Original) - Clipped from right */}
                {activePhoto.afterUrl && activePhoto.status === 'ready' && <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{
            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
          }} data-id="element-221">
                    <img src={activePhoto.beforeUrl} alt="Original" className="absolute inset-0 w-full h-full object-cover max-w-none" style={{
              width: '100%',
              height: '100%',
              objectPosition: 'left center'
            }} data-id="element-222" />
                  </div>}

                {/* Processing Overlay */}
                {activePhoto.status === 'processing' && <div className="absolute inset-0 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center text-white z-30" data-id="element-223">
                    <Loader2 className="w-12 h-12 animate-spin mb-4 text-cyan-400" data-id="element-224" />
                    <p className="text-lg font-medium" data-id="element-225">
                      AI is enhancing your photo...
                    </p>
                    <p className="text-sm text-white/60 mt-2" data-id="element-226">
                      Applying magazine-quality lighting and composition
                    </p>
                  </div>}

                {/* Failed Overlay */}
                {activePhoto.status === 'failed' && <div className="absolute inset-0 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center text-white z-30" data-id="element-227">
                    <AlertCircle className="w-12 h-12 mb-4 text-red-400" data-id="element-228" />
                    <p className="text-lg font-medium" data-id="element-229">Enhancement failed</p>
                    <p className="text-sm text-white/60 mt-2" data-id="element-230">
                      Please try capturing another photo.
                    </p>
                  </div>}

                {/* Slider Handle & Hint */}
                {activePhoto.status === 'ready' && activePhoto.afterUrl && <motion.div className="absolute top-0 bottom-0 cursor-ew-resize z-20 flex items-center justify-center" style={{
            left: hasInteracted ? `${sliderPosition}%` : '0%',
            transform: 'translateX(-50%)',
            width: '48px'
          }} animate={hasInteracted ? undefined : sliderControls} onMouseDown={handlePointerDown} onTouchStart={handlePointerDown} data-id="element-231">
                    {/* Visible white line */}
                    <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[3px] bg-white shadow-[0_0_8px_rgba(255,255,255,0.6)]" data-id="element-232" />

                    {/* Large grab handle */}
                    <motion.div className="relative w-14 h-14 bg-white rounded-full shadow-[0_0_20px_rgba(255,255,255,0.4),0_4px_12px_rgba(0,0,0,0.3)] flex items-center justify-center cursor-ew-resize hover:scale-110 active:scale-95 transition-transform" animate={!hasInteracted ? {
              scale: [1, 1.1, 1],
              boxShadow: ['0 0 20px rgba(255,255,255,0.4), 0 4px 12px rgba(0,0,0,0.3)', '0 0 30px rgba(6,182,212,0.6), 0 4px 16px rgba(0,0,0,0.3)', '0 0 20px rgba(255,255,255,0.4), 0 4px 12px rgba(0,0,0,0.3)']
            } : undefined} transition={!hasInteracted ? {
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            } : undefined} data-id="element-233">
                      <ChevronLeft className="w-5 h-5 text-gray-500" data-id="element-234" />
                      <div className="w-[2px] h-5 bg-gray-300 mx-0.5 rounded-full" data-id="element-235" />
                      <ChevronRight className="w-5 h-5 text-gray-500" data-id="element-236" />
                    </motion.div>

                    {/* Discovery Hint */}
                    <AnimatePresence data-id="element-237">
                      {showHint && !hasInteracted && <motion.div initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} className="absolute top-1/2 left-[calc(100%+8px)] -translate-y-1/2 flex items-center gap-2 pointer-events-none" data-id="element-238">
                          <div className="px-4 py-2 rounded-xl bg-white text-gray-900 text-sm font-bold shadow-[0_0_20px_rgba(255,255,255,0.3)] whitespace-nowrap flex items-center gap-2" data-id="element-239">
                            <ChevronRight className="w-4 h-4 text-cyan-500" data-id="element-240" />
                            Drag to compare original
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>}

                {/* Labels */}
                {activePhoto.status === 'ready' && activePhoto.afterUrl && <>
                    <div className="absolute top-6 left-6 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md text-white text-xs font-bold uppercase tracking-wider pointer-events-none transition-opacity duration-300" style={{
              opacity: sliderPosition > 10 ? 1 : 0
            }} data-id="element-241">
                      Original
                    </div>
                    <div className="absolute top-6 right-6 px-3 py-1.5 rounded-full bg-cyan-500/80 backdrop-blur-md text-white text-xs font-bold uppercase tracking-wider pointer-events-none transition-opacity duration-300 flex items-center gap-1.5" style={{
              opacity: sliderPosition < 90 ? 1 : 0
            }} data-id="element-242">
                      <Sparkles className="w-3 h-3" data-id="element-243" />
                      AI Enhanced
                    </div>
                  </>}
              </motion.div>}
          </div>

          {/* Bottom Bar */}
          <div className="absolute bottom-0 left-0 right-0 z-20 p-6 bg-gradient-to-t from-black/90 via-black/60 to-transparent" data-id="element-244">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-245">
              {/* Left: Badge */}
              <div className="flex-1" data-id="element-246">
                {activePhoto?.status === 'ready' && <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400" data-id="element-247">
                    <Sparkles className="w-4 h-4" data-id="element-248" />
                    <span className="text-sm font-bold uppercase tracking-wider" data-id="element-249">
                      Magazine Quality
                    </span>
                  </div>}
              </div>

              {/* Center: Email Form */}
              <div className="flex-1 max-w-md w-full" data-id="element-250">
                <form onSubmit={handleSend} className="relative" data-id="element-251">
                  <input type="email" placeholder="Send high-res photo to your email..." value={email} onChange={e => setEmail(e.target.value)} required className="w-full pl-4 pr-32 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-cyan-400 focus:bg-white/15 transition-all" data-id="element-252" />
                  <button type="submit" disabled={isSending || sendSuccess || !email} className={`absolute right-1.5 top-1.5 bottom-1.5 px-4 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${sendSuccess ? 'bg-emerald-500 text-white' : 'bg-cyan-500 hover:bg-cyan-400 text-white disabled:opacity-50 disabled:hover:bg-cyan-500'}`} data-id="element-253">
                    {isSending ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-254" /> : sendSuccess ? <>
                        <CheckCircle2 className="w-4 h-4" data-id="element-255" />
                        Sent
                      </> : <>
                        <Send className="w-4 h-4" data-id="element-256" />
                        Send
                      </>}
                  </button>
                </form>
              </div>

              {/* Right: Thumbnails */}
              <div className="flex-1 flex justify-end" data-id="element-257">
                {photos.length > 1 && <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar max-w-sm" data-id="element-258">
                    {photos.map(photo => <button key={photo.id} onClick={() => setActivePhotoId(photo.id)} className={`relative w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${activePhotoId === photo.id ? 'border-cyan-400 scale-105 shadow-lg shadow-cyan-500/20' : 'border-white/20 hover:border-white/40 opacity-60 hover:opacity-100'}`} data-id="element-259">
                        <img src={photo.beforeUrl} alt="" className="w-full h-full object-cover" data-id="element-260" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40" data-id="element-261">
                          {photo.status === 'processing' && <Loader2 className="w-5 h-5 text-white animate-spin" data-id="element-262" />}
                          {photo.status === 'ready' && activePhotoId !== photo.id && <CheckCircle2 className="w-5 h-5 text-emerald-400" data-id="element-263" />}
                          {photo.status === 'failed' && <AlertCircle className="w-5 h-5 text-red-400" data-id="element-264" />}
                        </div>
                      </button>)}
                  </div>}
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}